import type { PeerMetadata, SignalingGenerationV1 } from '@playcastdotio/Messaging';
import { ReceiverLatencyHintController } from '../receiverHints';
import {
  WebRtcOptimizationManager,
  configFromMetadata,
  type WebRtcOptimizationConfig,
} from '../WebRtcOptimizationManager';
import type { SendCodecDecision } from '../hardwareCodecPolicy';

export interface SenderRecoveryLimit {
  maxBitrateBps: number;
  maxFramerate: number;
}

export interface PeerMediaPolicyBundle {
  receiverPolicy: ReceiverLatencyHintController;
  generation: SignalingGenerationV1;
}

export interface PeerMediaPolicyLifecycle {
  readonly bundle: PeerMediaPolicyBundle;
  readonly codecDecision: SendCodecDecision<RTCRtpCodec> | null;
  beginConnection(connection: RTCPeerConnection, metadata: PeerMetadata,
    configOverride?: WebRtcOptimizationConfig): SignalingGenerationV1;
  beforeNegotiation(connection: RTCPeerConnection): Promise<void>;
  afterNegotiation(connection: RTCPeerConnection): Promise<SendCodecDecision<RTCRtpCodec> | null>;
  afterTrackReplacement(sender: RTCRtpSender,
    recoveryLimit?: SenderRecoveryLimit): Promise<SendCodecDecision<RTCRtpCodec> | null>;
  afterReceiverReplacement(connection: RTCPeerConnection): Promise<void>;
  validateInboundGeneration(value?: SignalingGenerationV1): boolean;
}

export interface PeerMediaPolicyLifecycleOptions {
  optimizationManager?: WebRtcOptimizationManager;
  receiverPolicy?: ReceiverLatencyHintController;
  createSessionId?: () => string;
}

function defaultSessionId(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  return cryptoApi?.randomUUID?.() ?? `peer-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * One policy owner for one peer-session object. It keeps generation identity stable while
 * replacement RTCPeerConnections advance the epoch and receive the same media policy.
 */
export class PeerMediaPolicyLifecycleManager implements PeerMediaPolicyLifecycle {
  public readonly bundle: PeerMediaPolicyBundle;

  private readonly optimizationManager: WebRtcOptimizationManager;
  private config: WebRtcOptimizationConfig | null = null;
  private connection: RTCPeerConnection | null = null;
  private currentCodecDecision: SendCodecDecision<RTCRtpCodec> | null = null;
  private remoteRequiresGeneration = false;
  private connectionCount = 0;

  constructor(options: PeerMediaPolicyLifecycleOptions = {}) {
    this.optimizationManager = options.optimizationManager ?? new WebRtcOptimizationManager();
    this.bundle = {
      receiverPolicy: options.receiverPolicy ?? new ReceiverLatencyHintController(),
      generation: { version: 1, sessionId: (options.createSessionId ?? defaultSessionId)(), epoch: 0 },
    };
  }

  public get codecDecision(): SendCodecDecision<RTCRtpCodec> | null {
    return this.currentCodecDecision;
  }

  public beginConnection(connection: RTCPeerConnection, metadata: PeerMetadata,
    configOverride?: WebRtcOptimizationConfig): SignalingGenerationV1 {
    if (this.connection !== connection) {
      if (this.connectionCount > 0) this.bundle.generation.epoch += 1;
      this.connectionCount += 1;
      this.connection = connection;
      this.currentCodecDecision = null;
    }
    this.config = configOverride ?? configFromMetadata(metadata);
    return { ...this.bundle.generation };
  }

  public beforeNegotiation(connection: RTCPeerConnection): Promise<void> {
    return this.config
      ? this.optimizationManager.applyPreConnectionOptimizations(connection, this.config)
      : Promise.resolve();
  }

  public async afterNegotiation(connection: RTCPeerConnection): Promise<SendCodecDecision<RTCRtpCodec> | null> {
    if (!this.config) return null;
    this.optimizationManager.applyPostConnectionOptimizations(connection, this.config);
    this.currentCodecDecision = await this.optimizationManager.pinSenderToHardwareCodec(connection, this.config);
    this.bundle.receiverPolicy.attachVideoReceiverOf(connection);
    // Pass the engine-resolved target so Firefox's explicit 0 wins here too.
    this.bundle.receiverPolicy.attachAudioReceiverOf(connection, this.config.audioJitterBufferTarget);
    return this.currentCodecDecision;
  }

  public async afterTrackReplacement(sender: RTCRtpSender,
    recoveryLimit?: SenderRecoveryLimit): Promise<SendCodecDecision<RTCRtpCodec> | null> {
    if (!this.config) return null;
    this.optimizationManager.applySenderParametersToSender(sender, this.config, recoveryLimit);
    this.currentCodecDecision = await this.optimizationManager.pinSenderToHardwareCodecSender(sender, this.config);
    return this.currentCodecDecision;
  }

  public async afterReceiverReplacement(connection: RTCPeerConnection): Promise<void> {
    if (!this.config) return;
    this.bundle.receiverPolicy.attachVideoReceiverOf(connection);
    this.bundle.receiverPolicy.attachAudioReceiverOf(connection, this.config.audioJitterBufferTarget);
  }

  public validateInboundGeneration(value?: SignalingGenerationV1): boolean {
    if (!value) return !this.remoteRequiresGeneration;
    if (value.version !== 1 || !value.sessionId.trim() || !Number.isInteger(value.epoch) || value.epoch < 0) {
      return false;
    }
    this.remoteRequiresGeneration = true;
    return value.sessionId === this.bundle.generation.sessionId
      && value.epoch === this.bundle.generation.epoch;
  }
}

